<template>
    <div class="stats-table">
        <div class="stats-table__bar">
            <v-text-field
                v-model="search"
                class="stats-table__search"
                outlined
                dense
                hide-details
                clearable
                color="teal"
                prepend-inner-icon="mdi-magnify"
                :label="generic ? 'Αναζήτηση παίκτη ή ομάδας' : 'Αναζήτηση παίκτη'"
            />
            <span class="stats-table__count">{{ activePlayers.length }} παίκτες</span>
        </div>
        <v-data-table
            :headers="headers"
            :items="activePlayers"
            :search="search"
            :custom-filter="filterPlayers"
            sort-by="points"
            sort-desc
            class="cl-table"
            hide-default-footer
            fixed-header
            :height="activePlayers.length > 12 ? 640 : undefined"
            :mobile-breakpoint="0"
            :items-per-page="-1"
            dark
            no-data-text="Δεν υπάρχουν ακόμη στατιστικά"
            no-results-text="Δεν βρέθηκε παίκτης"
            @click:row="goToPlayer"
        >
            <!-- the player, with the team and the jersey under the name: one column instead of three -->
            <template v-slot:item.player="{ item }">
                <span class="stats-table__who">
                    <img :src="`${assetsUrl}/${item.image}`" alt="" class="stats-table__photo" @error="$event.target.src = defaultPlayerImg">
                    <span class="stats-table__names">
                        <span class="stats-table__name">{{ item.player }}</span>
                        <span v-if="(generic && item.team) || item.jersey" class="stats-table__sub">
                            <span v-if="generic && item.teamLogo" class="cl-logo stats-table__mini-logo"><img :src="`${assetsUrl}/${item.teamLogo}`" alt=""></span>
                            <template v-if="generic && item.team">{{ item.team }}</template>
                            <template v-if="item.jersey"><template v-if="generic && item.team"> · </template>#{{ item.jersey }}</template>
                        </span>
                    </span>
                </span>
            </template>
            <template v-slot:item.two_points_percentage="{ item }">
                <span class="stats-table__pct">{{ item.two_points_percentage }}%</span>
                <span v-if="option" class="stats-table__ratio">{{ item.cum.twoPointsMade }}/{{ item.cum.twoPointAttempts }}</span>
            </template>
            <template v-slot:item.three_points_percentage="{ item }">
                <span class="stats-table__pct">{{ item.three_points_percentage }}%</span>
                <span v-if="option" class="stats-table__ratio">{{ item.cum.threePointsMade }}/{{ item.cum.threePointAttempts }}</span>
            </template>
            <template v-slot:item.free_throw_percentage="{ item }">
                <span class="stats-table__pct">{{ item.free_throw_percentage }}%</span>
                <span v-if="option" class="stats-table__ratio">{{ item.cum.freeThrowsMade }}/{{ item.cum.freeThrowAttempts }}</span>
            </template>
            <template v-slot:item.pir="{ item }">
                <span class="stats-table__eff">{{ item.pir }}</span>
            </template>
            <template v-slot:item.playTimeInSecs="{ item }">
                <span class="stats-table__min">
                    {{ option === 0 ? secToMinutesSeconds(item.avg.playTimeInSecs) :
                        secToMinutesSeconds(item.cum.playTimeInSecs) }}
                </span>
            </template>
        </v-data-table>
    </div>
</template>

<script>

    const gradients = [
        ['#ffd200'],
        ['#ffd200'],
        ['red', 'teal', 'yellow'],
        ['purple', 'violet'],
        ['#ffd200', '#F0F', '#FF0'],
        ['#f72047', '#ffd200', '#ffd200']
    ]

    export default {
        props: {
            players: {
                type: Array,
                default: () => []
            },
            option: {
                type: Number,
                default: 0
            },
            generic: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                headers: [
                    { text: 'Player', value: 'player', align: 'start' },
                    { text: 'GP', value: 'gp', align: 'start' },
                    // the minutes played (mm:ss), per game or in total
                    { text: 'MIN', value: 'playTimeInSecs', align: 'center' },
                    { text: 'Pts', value: 'points' },
                    { text: '2P%', value: 'two_points_percentage', align: 'center' },
                    { text: '3P%', value: 'three_points_percentage', align: 'center' },
                    { text: 'FT%', value: 'free_throw_percentage', align: 'center' },
                    { text: 'OR', value: 'offensiveRebounds', align: 'start' },
                    { text: 'DR', value: 'defensiveRebounds', align: 'start' },
                    { text: 'TR', value: 'rebounds', align: 'start' },
                    { text: 'AST', value: 'assists', align: 'start' },
                    { text: 'STL', value: 'steals', align: 'start' },
                    { text: 'TO', value: 'turnovers', align: 'start' },
                    { text: 'BLK', value: 'blockedShots', align: 'start' },
                    { text: 'FC', value: 'fouls', align: 'start' },
                    { text: 'EFF', value: 'pir', align: 'center' }
                ],
                maxGames: 1,
                search: null,
                defaultPlayerImg: require('@/assets/default_player.png'),
                assetsUrl: 'https://storage.googleapis.com',
                sparkline: {
                    width: 2,
                    radius: 10,
                    padding: 8,
                    lineCap: 'round',
                    gradient: gradients[5],
                    gradientDirection: 'top',
                    gradients,
                    fill: true,
                    type: 'trend',
                    autoLineWidth: false
                }
            }
        },
        computed: {
            activePlayers() {
                return (this.players || []).filter(player => player.active)
            }
        },
        watch: {
            option: {
                handler() {
                    this.prepareData()
                }
            },
            players: {
                handler() {
                    this.prepareData()
                }
            }
        },
        created() {
            // the team of a player is shown under the name (generic lists), not in a column of its own
            // Prepare data on component creation so initial players don't render undefined
            this.prepareData()
        },
        methods: {
            // the search looks at the name of the player and of the team only
            filterPlayers(value, search, item) {
                const text = (search || '').toLowerCase()
                return `${item.player || ''} ${item.team || ''}`.toLowerCase().includes(text)
            },
            goToPlayer(player) {
                this.$router.push({ name: 'Player', params: { id: player.id } })
            },
            secToMinutesSeconds(ms) {
                // no time kept for the player (no stats operator at his games)
                if (!ms) return '–'
                if (ms < 0) return '-'
                if (typeof ms !== 'number' || isNaN(ms)) return '-'
                const minutes = Math.floor(ms / 60)
                const seconds = Math.floor(ms % 60)
                if (isNaN(minutes) || isNaN(seconds)) return '-'
                return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
            },
            toFixedSafe(v, digits = 1) {
                const n = Number(v)
                return Number.isFinite(n) ? n.toFixed(digits) : '0.0'
            },
            pctSafe(made, attempts, digits = 1) {
                const m = Number(made) || 0
                const a = Number(attempts) || 0
                if (a === 0) return (0).toFixed(digits)
                return ((m / a) * 100).toFixed(digits)
            },
            numOrZero(v) {
                const n = Number(v)
                return Number.isFinite(n) ? n : 0
            },
            prepareData() {
                if (!Array.isArray(this.players)) return

                this.players.forEach(player => {
                    const avg = player?.avg || {}
                    const cum = player?.cum || {}
                    const statsLen = Array.isArray(player?.stats) ? player.stats.length : 0
                    if (statsLen > this.maxGames) this.maxGames = statsLen

                    // Ensure cum and avg objects exist and numeric fields default to 0
                    player.cum = Object.assign({
                        twoPointsMade: 0,
                        twoPointAttempts: 0,
                        threePointsMade: 0,
                        threePointAttempts: 0,
                        freeThrowsMade: 0,
                        freeThrowAttempts: 0,
                        points: 0,
                        offensiveRebounds: 0,
                        defensiveRebounds: 0,
                        rebounds: 0,
                        assists: 0,
                        steals: 0,
                        turnovers: 0,
                        blockedShots: 0,
                        personalFouls: 0,
                        fouls: 0,
                        pir: 0,
                        playTimeInSecs: 0
                    }, cum)

                    player.avg = Object.assign({
                        twoPointsMade: 0,
                        twoPointAttempts: 0,
                        threePointsMade: 0,
                        threePointAttempts: 0,
                        freeThrowsMade: 0,
                        freeThrowAttempts: 0,
                        points: 0,
                        offensiveRebounds: 0,
                        defensiveRebounds: 0,
                        rebounds: 0,
                        assists: 0,
                        steals: 0,
                        turnovers: 0,
                        blockedShots: 0,
                        personalFouls: 0,
                        fouls: 0,
                        pir: 0,
                        playTimeInSecs: 0
                    }, avg)

                    // Ensure fields exist to prevent template errors
                    player.player = `${player.name || ''} ${player.surname || ''}`.trim()
                    player.gp = statsLen

                    // Points and common stats
                    player.points = this.option ? this.numOrZero(cum.points) : this.toFixedSafe(avg.points, 1)
                    player.offensiveRebounds = this.option ? this.numOrZero(cum.offensiveRebounds) : this.toFixedSafe(avg.offensiveRebounds, 1)
                    player.defensiveRebounds = this.option ? this.numOrZero(cum.defensiveRebounds) : this.toFixedSafe(avg.defensiveRebounds, 1)
                    player.rebounds = this.option ? this.numOrZero(cum.rebounds) : this.toFixedSafe(avg.rebounds, 1)
                    player.assists = this.option ? this.numOrZero(cum.assists) : this.toFixedSafe(avg.assists, 1)
                    player.steals = this.option ? this.numOrZero(cum.steals) : this.toFixedSafe(avg.steals, 1)
                    player.turnovers = this.option ? this.numOrZero(cum.turnovers) : this.toFixedSafe(avg.turnovers, 1)
                    player.blockedShots = this.option ? this.numOrZero(cum.blockedShots) : this.toFixedSafe(avg.blockedShots, 1)
                    // every foul: personal, technical and unsportsmanlike
                    player.fouls = this.option ? this.numOrZero(cum.fouls) : this.toFixedSafe(avg.fouls, 1)

                    // Percentages
                    player.two_points_percentage = this.option
                        ? this.pctSafe(cum.twoPointsMade, cum.twoPointAttempts, 1)
                        : this.pctSafe(avg.twoPointsMade, avg.twoPointAttempts, 1)

                    player.free_throw_percentage = this.option
                        ? this.pctSafe(cum.freeThrowsMade, cum.freeThrowAttempts, 1)
                        : this.pctSafe(avg.freeThrowsMade, avg.freeThrowAttempts, 1)

                    player.three_points_percentage = this.option
                        ? this.pctSafe(cum.threePointsMade, cum.threePointAttempts, 1)
                        : this.pctSafe(avg.threePointsMade, avg.threePointAttempts, 1)

                    // PIR and play time
                    player.pir = this.option ? this.numOrZero(cum.pir) : this.toFixedSafe(avg.pir, 1)
                    // Ensure averagePir always exists to avoid template toFixed error
                    if (player.averagePir == null) player.averagePir = 0

                    // Normalize play time
                    player.playTimeInSecs = this.option
                        ? this.numOrZero(cum.playTimeInSecs)
                        : this.numOrZero(avg.playTimeInSecs)
                })
            }
        }
    }
</script>

<style scoped>
.stats-table__bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.stats-table__search {
    max-width: 320px;
}

.stats-table__count {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    color: rgba(35, 35, 35, 0.55);
}

.stats-table ::v-deep td {
    white-space: nowrap;
}

/* the sticky first column of the site's tables, on the charcoal of this one */
.stats-table ::v-deep table > tbody > tr > td:nth-child(1),
.stats-table ::v-deep table > thead > tr > th:nth-child(1) {
    position: sticky !important;
    left: 0;
    z-index: 2;
    background: #232323 !important;
    box-shadow: 1px 0 0 rgba(255, 255, 255, 0.08);
}

.stats-table ::v-deep table > thead > tr > th:nth-child(1) {
    z-index: 3;
    background: #1b1b1b !important;
}

.stats-table ::v-deep tbody tr {
    cursor: pointer;
}

.stats-table__who {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
}

.stats-table__photo {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #2e2e2e;
    object-fit: cover;
    object-position: top;
}

.stats-table__logo {
    width: 30px;
    height: 30px;
}

.stats-table__pct {
    font-weight: 700;
}

.stats-table__ratio {
    display: block;
    margin-left: 0;
    font-size: 0.72rem;
    line-height: 1;
    color: rgba(255, 255, 255, 0.5);
}

.stats-table__min {
    color: rgba(255, 255, 255, 0.8);
    font-variant-numeric: tabular-nums;
}

.stats-table__eff {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.2rem;
    color: #03e5b7;
}

/* compact: every column fits a laptop without scrolling */
.stats-table ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.stats-table ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    padding: 0 7px !important;
}

.stats-table ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th:first-child,
.stats-table ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td:first-child {
    padding-left: 14px !important;
}

.stats-table ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    height: 46px !important;
    font-size: 0.85rem !important;
}

.stats-table__names {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.2;
}

.stats-table__name {
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.stats-table__sub {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 210px;
    margin-top: 2px;
    overflow: hidden;
    font-size: 0.7rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.55);
    text-overflow: ellipsis;
}

.stats-table__mini-logo {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    border-radius: 3px;
}

@media (max-width: 700px) {
    .stats-table__name,
    .stats-table__sub {
        max-width: 130px;
    }

    .stats-table__photo {
        display: none;
    }
}
</style>
